import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

// Feature flags context
const SubscriptionContext = createContext(null);

// Plan hierarchy for comparison - WORLD-CLASS Janeiro 2026
const PLAN_HIERARCHY = {
  // Novos planos World-Class
  'trial': 0,
  'free': 0,
  'unlimited': 2,      // €12.99/mês - Netflix do Keto
  '8k': 3,             // €99 - Desafio 8K (topo)
  'challenge_8k': 3,
  'desafio_8k': 3,     // Backend subscription_type key (permissions_world_class)
  
  // Aliases para compatibilidade com código antigo
  'starter': 2,
  'premium': 2,
  'essential': 2,
  'base': 2,
  'student': 2,
  'maintenance': 2,
  'advanced': 2,
  'keto21': 2,
  'vip': 3,
  'lifetime': 3,
  
  // Full access para teste
  'full_access': 999,
  'tester': 999,
  'admin': 999,        // Admin tem acesso total
  'admin_grant': 999,  // Admin grant também tem acesso total
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState({});
  const [aiCredits, setAiCredits] = useState({ remaining: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  
  // 🏆 WORLD-CLASS: Novo estado de permissões
  const [permissions, setPermissions] = useState(null);
  
  // Plan simulation for admin testing
  const [simulatedPlan, setSimulatedPlan] = useState(() => {
    return localStorage.getItem('simulatedPlan') || null;
  });

  // Listen for simulation changes from PlanSimulator
  useEffect(() => {
    const handleSimulationChange = () => {
      setSimulatedPlan(localStorage.getItem('simulatedPlan') || null);
    };
    window.addEventListener('planSimulationChanged', handleSimulationChange);
    return () => window.removeEventListener('planSimulationChanged', handleSimulationChange);
  }, []);

  const clearSimulation = useCallback(() => {
    localStorage.removeItem('simulatedPlan');
    setSimulatedPlan(null);
  }, []);

  // 🏆 WORLD-CLASS: Buscar permissões do novo endpoint
  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      const response = await axios.get(`${API}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPermissions(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions(null);
      return null;
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      // Buscar tudo em paralelo
      const [subResponse, creditsResponse, permsData] = await Promise.all([
        axios.get(`${API}/subscription/current`).catch(() => ({ data: { plan: 'free' } })),
        axios.get(`${API}/ai-credits/balance`).catch(() => ({ data: { credits_remaining: 0, credits_total: 0 } })),
        fetchPermissions()
      ]);
      
      // Usar permissões World-Class se disponíveis, senão fallback
      const planFromPermissions = permsData?.subscription;
      
      setSubscription({
        ...subResponse.data,
        plan: planFromPermissions || subResponse.data?.plan || 'free',
        worldClass: permsData
      });
      setFeatures(subResponse.data?.features || {});
      setAiCredits({
        remaining: creditsResponse.data?.credits_remaining || 0,
        total: creditsResponse.data?.credits_total || 0,
        usedThisMonth: creditsResponse.data?.credits_used_this_month || 0,
        nextReset: creditsResponse.data?.next_reset
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Default to free plan
      setSubscription({ plan: 'free', status: 'free' });
      setFeatures({});
      setAiCredits({ remaining: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, [fetchSubscription]);

  // Re-fetch subscription when token changes (login/logout)
  useEffect(() => {
    const handleAuthChange = () => {
      const token = localStorage.getItem('token');
      if (token) {
        fetchSubscription();
      } else {
        setSubscription({ plan: 'free', status: 'free' });
        setFeatures({});
        setAiCredits({ remaining: 0, total: 0 });
      }
    };
    
    window.addEventListener('authStateChanged', handleAuthChange);
    return () => window.removeEventListener('authStateChanged', handleAuthChange);
  }, [fetchSubscription]);

  const currentPlan = subscription?.plan || 'free';

  const hasFeature = useCallback((featureName) => {
    const featureValue = features[featureName];
    if (featureValue === true || featureValue === 'full' || featureValue === 'vip') {
      return true;
    }
    if (featureValue === 'limited') {
      return 'limited';
    }
    return false;
  }, [features]);

  const hasMinPlan = useCallback((requiredPlan) => {
    const currentLevel = PLAN_HIERARCHY[currentPlan] || 0;
    const requiredLevel = PLAN_HIERARCHY[requiredPlan] || 0;
    return currentLevel >= requiredLevel;
  }, [currentPlan]);

  const canUseAI = useCallback(() => {
    return aiCredits.remaining > 0;
  }, [aiCredits.remaining]);

  const consumeAICredit = useCallback(async (feature) => {
    try {
      const response = await axios.post(`${API}/ai-credits/consume`, null, {
        params: { feature }
      });
      
      if (response.data.success) {
        setAiCredits(prev => ({
          ...prev,
          remaining: response.data.credits_remaining
        }));
        return { success: true };
      }
    } catch (error) {
      if (error.response?.status === 402) {
        return { 
          success: false, 
          reason: 'no_credits',
          message: error.response.data.detail?.message || 'Sem créditos disponíveis'
        };
      }
      return { success: false, reason: 'error' };
    }
  }, []);

  const refreshSubscription = useCallback(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Use simulated plan if set, otherwise use real plan
  const effectivePlan = simulatedPlan || currentPlan;

  // Override hasMinPlan to use effective plan
  const hasMinPlanEffective = useCallback((requiredPlan) => {
    const currentLevel = PLAN_HIERARCHY[effectivePlan] || 0;
    const requiredLevel = PLAN_HIERARCHY[requiredPlan] || 0;
    return currentLevel >= requiredLevel;
  }, [effectivePlan]);

  // 🏆 WORLD-CLASS: Verificar feature usando novo sistema
  const canAccessFeature = useCallback((feature) => {
    if (!permissions) return hasMinPlanEffective('essential');
    
    switch (feature) {
      case 'ecosystem':
      case 'body_twin':
      case 'ai_coach':
      case 'training':
      case 'meal_plan':
      case 'fasting':
      case 'progress':
      case 'spirit':
        return permissions.can_access_ecosystem;
      
      case 'desafio_8k':
      case 'chronology':
      case 'spirit_link':
      case 'cohort':
        return permissions.can_access_desafio_8k;
      
      default:
        return permissions.can_access_ecosystem;
    }
  }, [permissions, hasMinPlanEffective]);

  const value = {
    subscription,
    currentPlan: effectivePlan, // Use effective plan
    realPlan: currentPlan, // Keep access to real plan
    features,
    aiCredits,
    loading,
    hasFeature,
    hasMinPlan: hasMinPlanEffective, // Use effective check
    canUseAI,
    consumeAICredit,
    refreshSubscription,
    isPremium: hasMinPlanEffective('essential'),
    isVIP: hasMinPlanEffective('vip'),
    
    // 🏆 WORLD-CLASS: Novos campos de permissões
    permissions,
    canAccessEcosystem: permissions?.can_access_ecosystem ?? hasMinPlanEffective('essential'),
    canAccessDesafio8K: permissions?.can_access_desafio_8k ?? permissions?.features?.DESAFIO_8K ?? hasMinPlanEffective('8k'),
    // 8K users get everything Unlimited has (and more). Recognize that here.
    isUnlimited: ['unlimited', 'desafio_8k', '8k', 'challenge_8k'].includes(permissions?.subscription) || hasMinPlanEffective('unlimited'),
    isTrial: permissions?.trial ?? false,
    trialDaysLeft: permissions?.trial_days_remaining ?? 0,
    canAccessFeature,
    refreshPermissions: fetchPermissions,
    
    // Simulation controls
    simulatedPlan,
    setSimulatedPlan: (plan) => {
      localStorage.setItem('simulatedPlan', plan);
      setSimulatedPlan(plan);
    },
    clearSimulation,
    isSimulating: !!simulatedPlan
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    // Return default values instead of throwing error
    return {
      currentPlan: 'free',
      planDetails: null,
      subscriptionStatus: 'inactive',
      isLoading: false,
      updatePlan: () => {},
      checkFeatureAccess: () => false
    };
  }
  return context;
};

export default SubscriptionContext;
